import { Store } from "@ngrx/store";
import { appActions, fromApp, fromProgram, programActions, RootState } from "./store";
import { AppRoutes } from "./enums";
import { SERVER_HEALTH_CHECK_TIMEOUT_MS, SHOPIFY_HOST } from "./constants";
import { environment } from "../environments/environment";
import { ShopifyAppBridgeService } from "./services/shopify-app-bridge/shopify-app-bridge.service";
import { Router } from "@angular/router";
import { firstValueFrom, retry, take, throwError, timeout, timer } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { ProgramService } from "./services/api";
import { isDialogHostPath } from "./utils/dialog-host/dialog-host";
import { prefetchEntryRoute } from "./utils/entry-route-prefetch/entry-route-prefetch";
import { homeInstallKey } from "./pages/home/home-install-key";

const STARTUP_RETRIES = 2;
const STARTUP_RETRY_DELAY_MS = 1000;

function isServerError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status >= 500 || error.status === 0;
  }
  // ShopifyInterceptor unwraps HttpErrorResponse to the response body
  if (error && typeof error === "object" && "statusCode" in error) {
    const statusCode = (error as Record<string, unknown>)["statusCode"];
    return typeof statusCode === "number" && (statusCode >= 500 || statusCode === 0);
  }
  return !(error instanceof Error);
}

// Only a 404 says the shop has no program; any other refusal (401, 403, 429) says nothing about it
function isProgramNotFound(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) return error.status === 404;
  return !!error && typeof error === "object" && "statusCode" in error && (error as Record<string, unknown>)["statusCode"] === 404;
}

export const initializeApp = (
  store: Store<RootState>,
  shopifyAppBridgeService: ShopifyAppBridgeService,
  router: Router,
  programService: ProgramService,
): (() => Promise<void>) =>
  async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbedded = shopifyAppBridgeService.isEmbedded;
    const shop = urlParams.get("shop") ?? (isEmbedded ? window.shopify.config.shop : null);

    // The router's initial navigation is off (app-routing.module): outside Shopify, and for the copy of the admin in a
    // Shopify modal, the URL is routed as is; inside Shopify the app routes once the program is confirmed below.
    if (!shop) {
      router.initialNavigation();
      return;
    }

    if (!isEmbedded) {
      const redirectUrl = `https://${environment.host}`;
      window.location.href = `https://${shop}/admin/oauth/authorize?client_id=${environment.shopifyClientId}&scope=&redirect_uri=${redirectUrl}`;
      return;
    }

    const host = urlParams.get("host") ?? window.shopify.config.host;
    const programSlug = shop.replace(SHOPIFY_HOST, "");
    const originalPath = window.location.pathname;

    await shopifyAppBridgeService.initializeAppBridge(host, programSlug, shop);
    // The copy of the admin inside a Shopify modal only renders its dialog: the page behind it already loaded the program
    if (isDialogHostPath(originalPath)) {
      router.initialNavigation();
      return;
    }
    // Back and forward keep working once the app has routed
    router.setUpLocationChangeListener();

    store.dispatch(programActions.getFeatures());
    store.dispatch(programActions.getIntegrations());

    // Bootstrap never blocks on the program GET below; the UI renders while program data streams in via the store.
    //
    // On a Shopify iframe reload the URL is reset to the app root (pathname "/"), so there
    // is no deep link to honor. Only in that exact case do we restore the last route the
    // merchant was on, read synchronously from the (already rehydrated) store. Any explicit
    // path is honored as-is.
    const pathFragments = originalPath.split("/").filter(Boolean);
    const readNow = <T>(selector: (state: RootState) => T): T => {
      let value: T;
      store.select(selector).pipe(take(1)).subscribe(current => (value = current));
      return value;
    };

    // The page's chunks download alongside the program GET, before it answers
    prefetchEntryRoute(pathFragments.length ? originalPath : (readNow(fromApp.selectLastRoute) ?? ""), programSlug);

    // A reinstall starts on Home, where the setup guide starts over: the last route belongs to the previous install
    const navigateToApp = (reinstalled = false): void => {
      if (pathFragments.length === 0) {
        const lastRoute = reinstalled ? null : readNow(fromApp.selectLastRoute);
        if (lastRoute) router.navigateByUrl(lastRoute);
        else if (reinstalled) router.navigate([programSlug, AppRoutes.home]);
        else router.navigate([programSlug]);
      } else {
        // Strip a leading slug so we never duplicate it when rebuilding the path.
        const subPath = pathFragments[0] === programSlug ? pathFragments.slice(1) : pathFragments;
        router.navigate([programSlug, ...subPath]);
      }
    };

    // The program rehydrated from the store may belong to a previous install (the shop reinstalled the app): nothing
    // navigates or asks with its id until the GET confirms it, so a reinstall never opens the old pages nor sends
    // requests for a program that no longer exists.
    const knownProgramId = readNow(fromProgram.selectProgram)?.id ?? null;
    // An older release forgot only the program when the shop had none: the install key it left still names that install
    const knownInstallKey = readNow(fromApp.selectHomeInstallKey);
    const loadProgramData = (programId: string): void => {
      store.dispatch(programActions.getProgramSubscriptionInfo({ params: { programId } }));
      store.dispatch(programActions.getEngagements());
    };

    void (async () => {
      try {
        // A new shop has nothing to show without it: a server error is asked again twice before the outage notice
        // A request that stalls never reaches ServerDownInterceptor: past the limit it fails like an outage (not a 404)
        const program$ = programService
          .programControllerGetProgramV1({ programId: programSlug })
          .pipe(timeout(SERVER_HEALTH_CHECK_TIMEOUT_MS));
        const program = await firstValueFrom(
          knownProgramId
            ? program$
            : program$.pipe(
              retry({
                count: STARTUP_RETRIES,
                delay: (error: unknown) => (isServerError(error) ? timer(STARTUP_RETRY_DELAY_MS) : throwError(() => error)),
              }),
            ),
        );

        // Another program under the same shop (a reinstall): forget whatever the previous install left in the store
        const reinstalled = knownProgramId
          ? program.id !== knownProgramId
          : !!knownInstallKey && knownInstallKey !== homeInstallKey(program);
        if (reinstalled) store.dispatch(programActions.programNotFound());

        store.dispatch(programActions.getProgramSuccess({ program, loadingFullScreen: false }));
        // A program whose onboarding was left half-way resumes it; otherwise a stale onboarding link lands on Home
        if (!reinstalled && readNow(fromApp.selectOnboardingPending)) router.navigate([programSlug, AppRoutes.onboarding]);
        else if (pathFragments.includes(AppRoutes.onboarding)) router.navigate([programSlug, AppRoutes.home]);
        else navigateToApp(reinstalled);
        loadProgramData(program.id);
      } catch (error: unknown) {
        // A shop we already know still opens with its program; a new one shows the outage notice, whose recovery reloads
        if (!isProgramNotFound(error)) {
          if (knownProgramId) {
            navigateToApp();
            loadProgramData(knownProgramId);
          } else {
            store.dispatch(appActions.setServerDown({ serverDown: true }));
          }
          return;
        }

        // No program for this shop: forget whatever a previous install left in the store
        store.dispatch(programActions.programNotFound());
        store.dispatch(appActions.setOnboardingPending({ pending: false }));
        router.navigate([programSlug, AppRoutes.onboarding]);
      }
    })();
  };
